import React from "react";
import {Area, AreaChart, ReferenceLine, XAxis, YAxis} from "recharts";
import Translations from "../../lang/en/pages.json";
import { formatNumberWithSuffix } from "../../utils";

interface DataPoint {
  x: number;
  y: number;
}

interface BellCurveProps {
  data: DataPoint[];
  width?: number;
  height?: number;
  confidenceMin?: number;
  confidenceMax?: number;
}

const BellCurve: React.FC<BellCurveProps> = ({
  data,
  width = 600,
  height = 300,
  confidenceMin,
  confidenceMax,
}) => {
  const domain = [
    Math.min(...data.map((d) => d.x)),
    Math.max(...data.map((d) => d.x)),
  ];

  const minLabel = domain[0];
  const maxLabel = domain[1];
  const centerLine = (domain[0] + domain[1]) / 2;
  const centerLabel = centerLine;

  const peakDataPoint = data.reduce(
    (prev, curr) => (curr.y > prev.y ? curr : prev),
    data[0]
  );
  const peakX = peakDataPoint.x;

  // Helper function to find y value for a given x
  const findYValueForX = (xValue: number): number => {
    // Find the closest x points on either side
    const leftPoint = data.reduce((prev, curr) =>
      curr.x <= xValue && curr.x > prev.x ? curr : prev,
      { x: -Infinity, y: 0 }
    );
    const rightPoint = data.reduce((prev, curr) =>
      curr.x >= xValue && curr.x < prev.x ? curr : prev,
      { x: Infinity, y: 0 }
    );

    // Linear interpolation between the points
    if (leftPoint.x === -Infinity || rightPoint.x === Infinity) {
      return 0;
    }

    if (leftPoint.x === rightPoint.x) {
      return leftPoint.y;
    }

    const ratio = (xValue - leftPoint.x) / (rightPoint.x - leftPoint.x);
    return leftPoint.y + ratio * (rightPoint.y - leftPoint.y);
  };

  // Create custom references lines that only extend to the curve
  const ReferenceLineToPoint = ({ x, color }: { x: number; color: string }) => {
    const yValue = findYValueForX(x);
    const svgHeight = height - 45; // Adjust based on chart margins
    const lineHeight = svgHeight * (1 - yValue / 100); // Convert percentage to pixels

    return (
      <ReferenceLine
        x={x}
        stroke={color}
        strokeDasharray="3 3"
        strokeWidth={1.5}
        ifOverflow="hidden"
        segment={[{ y: lineHeight }, { y: svgHeight }]}
      />
    );
  };

  return (
    <div>
      <AreaChart
        width={width}
        height={height}
        data={data}
        margin={{ top: 20, right: 110, left: 10, bottom: 15 }}
      >
        <XAxis
          label={{
            fontSize: "0.75rem",
            dy: 20,
            position: "center",
            value:
              Translations.pages.dashboard_page.tabs.overview.damages_block
                .x_axis.label,
            style: { fill: "rgba(0, 0, 0, 0.56)" },
          }}
          tick={({ x, y, payload }) => (
            <text
              x={x}
              y={y + 15}
              textAnchor="middle"
              fill={payload.value === centerLine ? "#00796B" : "rgba(0, 0, 0, 0.38)"}
            >
              {formatNumberWithSuffix(payload.value, true)}
            </text>
          )}
          tickLine={false}
          axisLine={false}
          dataKey="x"
          type="number"
          domain={domain}
          tickFormatter={(value) => {
            if (value === domain[0])
              return formatNumberWithSuffix(minLabel, true);
            if (value === centerLine)
              return formatNumberWithSuffix(centerLabel, true);
            if (value === domain[1])
              return formatNumberWithSuffix(maxLabel, true);
            return "";
          }}
          ticks={[domain[0], peakX, domain[1]]}
        />
        <svg>
          <path
            d={`
              M70,30   
              L70,${height - 55}
              A15,15 0 0 0 85,${height - 45} 
              L${width - 50},${height - 45}
            `}
            stroke="#666666"
            opacity={0.2}
            strokeWidth="1"
            fill="none"
          />
        </svg>
        <YAxis
          dy={0}
          dx={-15}
          tick={{ fill: "rgba(0, 0, 0, 0.38)" }}
          axisLine={false}
          tickLine={false}
          label={{
            value:
              Translations.pages.dashboard_page.tabs.overview.damages_block
                .y_axis.label,
            angle: -90,
            fontSize: "0.75rem",
            position: "left",
            dy: -60,
            dx: 20,
            style: { fill: "rgba(0, 0, 0, 0.56)" },
            offset: 0,
          }}
          hide={false}
          domain={[0, 100]}
          tickCount={2}
        />

        <ReferenceLine
          x={peakX}
          stroke="#2E8B57"
          strokeDasharray="3 3"
          strokeWidth={1.5}
        />
        {confidenceMin !== undefined && (
          <ReferenceLineToPoint x={confidenceMin} color="#2E8B57" />
        )}
        {confidenceMax !== undefined && (
          <ReferenceLineToPoint x={confidenceMax} color="#2E8B57" />
        )}

        <Area
          type="monotone"
          dataKey="y"
          stroke="#D0E8D7"
          fill="#D0E8D7"
          fillOpacity={0.3}
        />
      </AreaChart>
    </div>
  );
};

export default BellCurve;